import { PropsWithChildren, useEffect, useState } from 'react';
import { app, authentication } from '@microsoft/teams-js';
import AuthContext from './authContext';
import { AppType, AuthData, User } from './interfaces';
import { Loader } from '@common/ui/Loader';
import { generatePath, useNavigate } from 'react-router-dom';
import routes from '@routes';

import { showAdminPortal, onSetUpMfa, onEnterMfa, switchTenant } from './common';

const NotificationType = {
  GranteeRequestPendingApproval: 'grantee_request_pending_approval',
  GranteeRequestRejected: 'grantee_request_rejected',
  GranteeRequestApproved: 'grantee_request_approved',
  GranteeRequestGranted: 'grantee_request_granted',
  GranteeRequestMFASubmitted: 'grantee_request_pending_mfa',
  GranteeRequestGoingToBeRevoked: 'grantee_request_going_to_be_revoked',
  GranteeRequestRevoked: 'grantee_request_revoked',
  GranteeRequestFailed: 'grantee_request_failed',
  GranteeResetCredentialSucceeded: 'grantee_reset_credentials_success',
  GranteeResetCredentialFailed: 'grantee_reset_credentials_failure',
  AdminRequestFailed: 'admin_request_failed_to_admin',
  ApproverRequestPendingApproval: 'approver_request_pending_approval',
  ApproverRequestApprovalFinished: 'approver_approver_request_approval_finished',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

const TeamsAuthProvider = ({ children }: PropsWithChildren<unknown>) => {
  const [authData, setAuthData] = useState<AuthData | null>(null);
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const teamsAuth = async function () {
      await app.initialize();
      const context = await app.getContext();
      const token = await authentication.getAuthToken();
      app.notifySuccess();

      const teamsUser: User = {
        id: context.user?.id || '',
        email: context.user?.userPrincipalName || '',
        name: context.user?.displayName || '',
        app: AppType.Teams,
        tenantId: '',
      };

      setAuthData({
        user: teamsUser,
        accessToken: token,
        tenants: [],
        isMfaPassed: false,
        isMfaEnrolled: false,
      });

      const notification = getNotification(context);
      if (notification) {
        let navigateUrl: string | undefined;
        let messageType: string | undefined;

        const taskUrl = generatePath(routes.TaskDetails.path, { id: notification.friendlyRequestId });
        const requestUrl = generatePath(routes.RequestDetails.path, { id: notification.friendlyRequestId });

        switch (notification.notificationType) {
          case NotificationType.GranteeRequestPendingApproval:
          case NotificationType.GranteeRequestMFASubmitted:
          case NotificationType.GranteeRequestGoingToBeRevoked:
          case NotificationType.GranteeRequestRevoked:
            navigateUrl = requestUrl;
            break;
          case NotificationType.GranteeRequestApproved:
          case NotificationType.GranteeRequestGranted:
          case NotificationType.GranteeResetCredentialSucceeded:
            messageType = 'success';
            navigateUrl = requestUrl;
            break;
          case NotificationType.GranteeRequestRejected:
          case NotificationType.GranteeRequestFailed:
          case NotificationType.GranteeResetCredentialFailed:
            messageType = 'error';
            navigateUrl = requestUrl;
            break;
          case NotificationType.AdminRequestFailed:
            messageType = 'error';
            navigateUrl = taskUrl;
            break;
          case NotificationType.ApproverRequestPendingApproval:
            navigateUrl = taskUrl;
            break;
          case NotificationType.ApproverRequestApprovalFinished:
            messageType = 'success';
            navigateUrl = taskUrl;
            break;
          default:
            navigateUrl = requestUrl;
        }

        if (notification.message) {
          navigateUrl += `?message=${notification.message}`;
          if (messageType) {
            navigateUrl += `&message_type=${messageType}`;
          }
        }

        if (navigateUrl) {
          navigate(navigateUrl, { replace: true });
        }
      }

      setIsReady(true);
    };

    teamsAuth().catch(app.notifyFailure);
  }, [navigate]);

  if (!authData || !isReady) {
    return <Loader />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...authData,
        switchTenant,
        showAdminPortal,
        onSetUpMfa,
        onEnterMfa,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default TeamsAuthProvider;

function getNotification(context: app.Context):
  | {
      friendlyRequestId: string;
      requestId: string;
      message: string;
      notificationType: NotificationType;
    }
  | undefined {
  const { subPageId } = context.page;

  if (!subPageId) {
    return;
  }

  let parsedPayload;
  try {
    parsedPayload = JSON.parse(subPageId);
  } catch (error) {
    return;
  }

  const { friendly_request_id, request_id, message, notification_type } = parsedPayload;

  if (!friendly_request_id || !request_id || !message || !notification_type || !isNotificationType(notification_type)) {
    return;
  }

  return {
    friendlyRequestId: friendly_request_id,
    requestId: request_id,
    message,
    notificationType: notification_type,
  };
}

function isNotificationType(notificationType: string): notificationType is NotificationType {
  return Object.values(NotificationType).includes(notificationType as NotificationType);
}
